import apiClient from './apiClient';

class TaskService {
  constructor() {
    this.baseURL = '/tasks';
  }

  /**
   * Get all tasks with filtering and pagination
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Task list response
   */
  async getTasks(params = {}) {
    try {
      const response = await apiClient.get(this.baseURL, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get tasks assigned to current user
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} My tasks response
   */
  async getMyTasks(params = {}) {
    try {
      const response = await apiClient.get(`${this.baseURL}/my`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching my tasks:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get a single task by ID
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Task details
   */
  async getTask(taskId) {
    try {
      const response = await apiClient.get(`${this.baseURL}/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching task:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create a new task
   * @param {Object} taskData - Task creation data
   * @returns {Promise<Object>} Created task
   */
  async createTask(taskData) {
    try {
      this.validateTaskData(taskData);
      const response = await apiClient.post(this.baseURL, taskData);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Update an existing task
   * @param {string} taskId - Task ID
   * @param {Object} taskData - Task update data
   * @returns {Promise<Object>} Updated task
   */
  async updateTask(taskId, taskData) {
    try {
      this.validateTaskData(taskData, false);
      const response = await apiClient.put(`${this.baseURL}/${taskId}`, taskData);
      return response.data;
    } catch (error) {
      console.error('Error updating task:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Delete a task
   * @param {string} taskId - Task ID
   * @returns {Promise<Object>} Deletion response
   */
  async deleteTask(taskId) {
    try {
      const response = await apiClient.delete(`${this.baseURL}/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting task:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Perform task action (start, complete, cancel, etc.)
   * @param {string} taskId - Task ID
   * @param {Object} actionData - Action data
   * @returns {Promise<Object>} Updated task
   */
  async performTaskAction(taskId, actionData) {
    try {
      this.validateTaskAction(actionData);
      const response = await apiClient.post(`${this.baseURL}/${taskId}/actions`, actionData);
      return response.data;
    } catch (error) {
      console.error('Error performing task action:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get task statistics
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Task statistics
   */
  async getTaskStats(params = {}) {
    try {
      const response = await apiClient.get(`${this.baseURL}/stats`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching task stats:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get available task types
   * @returns {Array} Task types
   */
  getTaskTypes() {
    return [
      { value: 'processing', label: 'Processing' },
      { value: 'scheduling', label: 'Scheduling' },
      { value: 'verification', label: 'Verification' },
      { value: 'admin', label: 'Administrative' },
      { value: 'follow_up', label: 'Follow Up' },
      { value: 'coordination', label: 'Coordination' }
    ];
  }

  /**
   * Get available priority levels
   * @returns {Array} Priority levels
   */
  getPriorityLevels() {
    return [
      { value: 'low', label: 'Low', color: 'green' },
      { value: 'medium', label: 'Medium', color: 'yellow' },
      { value: 'high', label: 'High', color: 'orange' },
      { value: 'urgent', label: 'Urgent', color: 'red' }
    ];
  }

  /**
   * Get available task statuses
   * @returns {Array} Task statuses
   */
  getTaskStatuses() {
    return [
      { value: 'pending', label: 'Pending', color: 'gray' },
      { value: 'in_progress', label: 'In Progress', color: 'blue' },
      { value: 'completed', label: 'Completed', color: 'green' },
      { value: 'cancelled', label: 'Cancelled', color: 'red' },
      { value: 'on_hold', label: 'On Hold', color: 'yellow' }
    ];
  }

  /**
   * Get available task categories
   * @returns {Array} Task categories
   */
  getTaskCategories() {
    return [
      { value: 'administrative', label: 'Administrative' },
      { value: 'interview', label: 'Interview' },
      { value: 'candidate', label: 'Candidate' },
      { value: 'system', label: 'System' },
      { value: 'communication', label: 'Communication' }
    ];
  }

  /**
   * Get available task actions
   * @returns {Array} Task actions
   */
  getTaskActions() {
    return [
      { value: 'start', label: 'Start Task', icon: 'Play' },
      { value: 'complete', label: 'Mark Complete', icon: 'CheckCircle' },
      { value: 'cancel', label: 'Cancel Task', icon: 'X' },
      { value: 'hold', label: 'Put on Hold', icon: 'Pause' },
      { value: 'resume', label: 'Resume Task', icon: 'Play' }
    ];
  }

  /**
   * Validate task data
   * @param {Object} taskData - Task data to validate
   * @param {boolean} isCreate - Whether this is for creation (requires all fields)
   */
  validateTaskData(taskData, isCreate = true) {
    const errors = {};

    if (isCreate || taskData.title !== undefined) {
      if (!taskData.title || taskData.title.trim().length === 0) {
        errors.title = 'Task title is required';
      } else if (taskData.title.length > 500) {
        errors.title = 'Task title must not exceed 500 characters';
      }
    }

    if (taskData.description && taskData.description.length > 2000) {
      errors.description = 'Description must not exceed 2000 characters';
    }

    if (isCreate || taskData.task_type !== undefined) {
      const validTypes = this.getTaskTypes().map(t => t.value);
      if (!taskData.task_type || !validTypes.includes(taskData.task_type)) {
        errors.task_type = 'Valid task type is required';
      }
    }

    if (isCreate || taskData.priority !== undefined) {
      const validPriorities = this.getPriorityLevels().map(p => p.value);
      if (!taskData.priority || !validPriorities.includes(taskData.priority)) {
        errors.priority = 'Valid priority level is required';
      }
    }

    if (isCreate || taskData.category !== undefined) {
      const validCategories = this.getTaskCategories().map(c => c.value);
      if (!taskData.category || !validCategories.includes(taskData.category)) {
        errors.category = 'Valid category is required';
      }
    }

    if (isCreate || taskData.assigned_to !== undefined) {
      if (!taskData.assigned_to) {
        errors.assigned_to = 'Task must be assigned to someone';
      }
    }

    if (taskData.status !== undefined) {
      const validStatuses = this.getTaskStatuses().map(s => s.value);
      if (!validStatuses.includes(taskData.status)) {
        errors.status = 'Valid status is required';
      }
    }

    if (taskData.due_date && new Date(taskData.due_date) < new Date()) {
      errors.due_date = 'Due date cannot be in the past';
    }

    if (taskData.estimated_duration && (taskData.estimated_duration < 1 || taskData.estimated_duration > 10080)) {
      errors.estimated_duration = 'Estimated duration must be between 1 and 10080 minutes (1 week)';
    }

    if (Object.keys(errors).length > 0) {
      throw new Error(`Validation failed: ${Object.values(errors).join(', ')}`);
    }
  }

  /**
   * Validate task action data
   * @param {Object} actionData - Action data to validate
   */
  validateTaskAction(actionData) {
    const validActions = this.getTaskActions().map(a => a.value);
    
    if (!actionData.action || !validActions.includes(actionData.action)) {
      throw new Error('Valid action is required');
    }

    if (actionData.action === 'complete' && actionData.actual_duration) {
      if (actionData.actual_duration < 1) {
        throw new Error('Actual duration must be at least 1 minute');
      }
    }
  }

  /**
   * Format task for display
   * @param {Object} task - Task object
   * @returns {Object} Formatted task
   */
  formatTask(task) {
    const priorityConfig = this.getPriorityLevels().find(p => p.value === task.priority);
    const statusConfig = this.getTaskStatuses().find(s => s.value === task.status);
    const typeConfig = this.getTaskTypes().find(t => t.value === task.task_type);
    const categoryConfig = this.getTaskCategories().find(c => c.value === task.category);

    return {
      ...task,
      formattedPriority: priorityConfig?.label || task.priority,
      priorityColor: priorityConfig?.color || 'gray',
      formattedStatus: statusConfig?.label || task.status,
      statusColor: statusConfig?.color || 'gray',
      formattedType: typeConfig?.label || task.task_type,
      formattedCategory: categoryConfig?.label || task.category,
      isOverdue: task.due_date && new Date(task.due_date) < new Date() && !['completed', 'cancelled'].includes(task.status),
      canStart: task.status === 'pending',
      canComplete: ['pending', 'in_progress'].includes(task.status),
      canCancel: ['pending', 'in_progress', 'on_hold'].includes(task.status),
      canHold: ['pending', 'in_progress'].includes(task.status),
      canResume: task.status === 'on_hold'
    };
  }

  /**
   * Handle API errors
   * @param {Error} error - API error
   * @returns {Error} Formatted error
   */
  handleError(error) {
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return new Error(data.error || 'Invalid request data');
        case 401:
          return new Error('Authentication required. Please log in again.');
        case 403:
          return new Error('You do not have permission to perform this action');
        case 404:
          return new Error('Task not found');
        case 422:
          return new Error(data.error || 'Validation failed');
        case 500:
          return new Error('Server error. Please try again later.');
        default:
          return new Error(data.error || 'An error occurred');
      }
    } else if (error.request) {
      return new Error('Network error. Please check your connection.');
    } else {
      return error;
    }
  }

  /**
   * Get filters for task list
   * @returns {Object} Available filters
   */
  getFilters() {
    return {
      statuses: this.getTaskStatuses(),
      priorities: this.getPriorityLevels(),
      types: this.getTaskTypes(),
      categories: this.getTaskCategories()
    };
  }

  /**
   * Build query params for API calls
   * @param {Object} filters - Filter values
   * @param {number} page - Page number
   * @param {number} pageSize - Page size
   * @returns {Object} Query params
   */
  buildQueryParams(filters = {}, page = 1, pageSize = 20) {
    const params = { page, page_size: pageSize };

    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params[key] = filters[key];
      }
    });

    return params;
  }
}

export default new TaskService();